import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AllelFrequencyCalculator = () => {
  const [populationData, setPopulationData] = useState({
    totalIndividuals: '',
    homozygousDominant: '',
    heterozygous: '',
    homozygousRecessive: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPopulationData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateAlleleFrequencies = () => {
    const { totalIndividuals, homozygousDominant, heterozygous, homozygousRecessive } = populationData;
    
    const total = parseFloat(totalIndividuals) || 0;
    const homoDom = parseFloat(homozygousDominant) || 0;
    const hetero = parseFloat(heterozygous) || 0;
    const homoRec = parseFloat(homozygousRecessive) || 0;

    if (total === 0 || isNaN(total)) {
      setError('Please enter a valid number for total individuals');
      return;
    }

    // Calculate allele frequencies
    const p = (2 * homoDom + hetero) / (2 * total);
    const q = (2 * homoRec + hetero) / (2 * total);

    // Calculate genotype frequencies
    const p2 = homoDom / total;
    const pq = hetero / total;
    const q2 = homoRec / total;

    // Hardy-Weinberg Equilibrium Check
    const expectedAA = p2 * total;
    const expectedAa = pq * total * 2;
    const expectedaa = q2 * total;

    // Chi-Square Test
    const chiSquare = 
      Math.pow(homoDom - expectedAA, 2) / expectedAA +
      Math.pow(hetero - expectedAa, 2) / expectedAa +
      Math.pow(homoRec - expectedaa, 2) / expectedaa;

    // Genetic Diversity Index
    const heterozygosity = 1 - (Math.pow(p, 2) + Math.pow(q, 2));

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Total individuals: ${total}`,
      `Homozygous dominant (AA): ${homoDom}`,
      `Heterozygous (Aa): ${hetero}`,
      `Homozygous recessive (aa): ${homoRec}`,
      '\nStep 2: Calculate allele frequencies',
      `Frequency of dominant allele (p) = (2 × AA + Aa) ÷ (2 × total)`,
      `Frequency of dominant allele (p) = (2 × ${homoDom} + ${hetero}) ÷ (2 × ${total}) = ${p.toFixed(3)}`,
      '\nStep 3: Calculate genotype frequencies',
      `Frequency of AA = ${homoDom} ÷ ${total} = ${p2.toFixed(3)}`,
      `Frequency of Aa = ${hetero} ÷ ${total} = ${pq.toFixed(3)}`,
      `Frequency of aa = ${homoRec} ÷ ${total} = ${q2.toFixed(3)}`
    ];

    const newResults = {
      alleleFrequencies: {
        dominant: p.toFixed(3),
        recessive: q.toFixed(3)
      },
      genotypeFrequencies: {
        homozygousDominant: p2.toFixed(3),
        heterozygous: pq.toFixed(3),
        homozygousRecessive: q2.toFixed(3)
      },
      calculationSteps,
      hardyWeinberg: {
        expectedAA: expectedAA.toFixed(2),
        expectedAa: expectedAa.toFixed(2),
        expectedaa: expectedaa.toFixed(2),
        chiSquare: chiSquare.toFixed(3),
        pValue: chiSquare > 5.991 ? "p < 0.05" : "p ≥ 0.05"
      },
      geneticDiversity: heterozygosity.toFixed(3)
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Allele p', 'Allele q', 'Genotype AA', 'Genotype Aa', 'Genotype aa'],
        datasets: [{
          label: 'Frequencies',
          data: [p, q, p2, pq, q2],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Allele p', 'Allele q'],
        datasets: [{
          data: [p, q],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const exampleData = {
    totalIndividuals: 100,
    homozygousDominant: 36,
    heterozygous: 48,
    homozygousRecessive: 16,
    alleleFrequencies: {
      dominant: 0.6,
      recessive: 0.4
    },
    genotypeFrequencies: {
      homozygousDominant: 0.36,
      heterozygous: 0.48,
      homozygousRecessive: 0.16
    }
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧬</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Allele Frequency Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Population Genetics Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate allele and genotype frequencies in a population using the Hardy-Weinberg principle. 
          This calculator helps you understand genetic diversity and population genetics concepts.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="totalIndividuals">
            Total Individuals:
          </label>
          <input
            type="number"
            id="totalIndividuals"
            name="totalIndividuals"
            value={populationData.totalIndividuals}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter total number of individuals"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="homozygousDominant">
            Homozygous Dominant (AA):
          </label>
          <input
            type="number"
            id="homozygousDominant"
            name="homozygousDominant"
            value={populationData.homozygousDominant}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter number of AA individuals"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="heterozygous">
            Heterozygous (Aa):
          </label>
          <input
            type="number"
            id="heterozygous"
            name="heterozygous"
            value={populationData.heterozygous}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter number of Aa individuals"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="homozygousRecessive">
            Homozygous Recessive (aa):
          </label>
          <input
            type="number"
            id="homozygousRecessive"
            name="homozygousRecessive"
            value={populationData.homozygousRecessive}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter number of aa individuals"
          />
        </div>

        <button
          onClick={calculateAlleleFrequencies}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Allele Frequencies
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Allele Frequencies</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Allele p (dominant)</span>
                    <span className="font-medium">{results.alleleFrequencies.dominant}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Allele q (recessive)</span>
                    <span className="font-medium">{results.alleleFrequencies.recessive}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Genotype Frequencies</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>AA (Homozygous Dominant)</span>
                    <span className="font-medium">{results.genotypeFrequencies.homozygousDominant}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Aa (Heterozygous)</span>
                    <span className="font-medium">{results.genotypeFrequencies.heterozygous}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>aa (Homozygous Recessive)</span>
                    <span className="font-medium">{results.genotypeFrequencies.homozygousRecessive}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Hardy-Weinberg Equilibrium</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Expected AA</span>
                    <span className="font-medium">{results.hardyWeinberg.expectedAA}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Expected Aa</span>
                    <span className="font-medium">{results.hardyWeinberg.expectedAa}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Expected aa</span>
                    <span className="font-medium">{results.hardyWeinberg.expectedaa}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Chi-Square</span>
                    <span className="font-medium">{results.hardyWeinberg.chiSquare}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>p-value</span>
                    <span className="font-medium">{results.hardyWeinberg.pValue}</span>
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Genetic Diversity</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Expected Heterozygosity</span>
                    <span className="font-medium">{results.geneticDiversity}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Frequency'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Genetic Component'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Allele and Genotype Frequencies'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Pie Chart</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Allele Frequency Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using sample population data:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Total individuals: {exampleData.totalIndividuals}</li>
              <li>Homozygous dominant (AA): {exampleData.homozygousDominant}</li>
              <li>Heterozygous (Aa): {exampleData.heterozygous}</li>
              <li>Homozygous recessive (aa): {exampleData.homozygousRecessive}</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Calculate allele frequencies:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Allele p = (2 × AA + Aa) ÷ (2 × total)</li>
                  <li>Allele p = (2 × {exampleData.homozygousDominant} + {exampleData.heterozygous}) ÷ (2 × {exampleData.totalIndividuals}) = {exampleData.alleleFrequencies.dominant}</li>
                  <li>Allele q = (2 × aa + Aa) ÷ (2 × total)</li>
                  <li>Allele q = (2 × {exampleData.homozygousRecessive} + {exampleData.heterozygous}) ÷ (2 × {exampleData.totalIndividuals}) = {exampleData.alleleFrequencies.recessive}</li>
                </ul>
              </li>
              <li>
                <p>Calculate genotype frequencies:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Frequency of AA = {exampleData.homozygousDominant} ÷ {exampleData.totalIndividuals} = {exampleData.genotypeFrequencies.homozygousDominant}</li>
                  <li>Frequency of Aa = {exampleData.heterozygous} ÷ {exampleData.totalIndividuals} = {exampleData.genotypeFrequencies.heterozygous}</li>
                  <li>Frequency of aa = {exampleData.homozygousRecessive} ÷ {exampleData.totalIndividuals} = {exampleData.genotypeFrequencies.homozygousRecessive}</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Allele Frequency Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Allele Frequency Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Prepare Your Population Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Collect genotype data for your population</li>
                <li>Count the number of individuals for each genotype</li>
                <li>Ensure accurate population size information</li>
                <li>Verify data completeness and accuracy</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Population Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the number of individuals with each genotype</li>
                <li>Input the total population size</li>
                <li>Select the appropriate allele notation (e.g., A/a)</li>
                <li>Click the "Calculate Allele Frequencies" button</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated allele frequencies</li>
                <li>Check the genotype frequencies</li>
                <li>Verify Hardy-Weinberg equilibrium</li>
                <li>Use the frequency chart for visual analysis</li>
                <li>Compare results with expected values</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Ensure accurate genotype counts</li>
                <li>Verify population size is representative</li>
                <li>Consider population substructure</li>
                <li>Check for selection pressures</li>
                <li>Use the frequency chart to identify unusual patterns</li>
                <li>Consider sample size effects on accuracy</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Allele Frequencies <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Allele frequencies are a measure of genetic variation in a population. Understanding these frequencies helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Population Genetics:</span> Studying genetic diversity and evolution
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Medical Genetics:</span> Understanding genetic disorders and carrier frequencies
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Conservation Biology:</span> Monitoring genetic health of endangered species
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Forensic Science:</span> DNA profiling and genetic matching
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: The Hardy-Weinberg principle assumes no mutation, migration, selection, or genetic drift. Real populations may deviate from these ideal conditions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AllelFrequencyCalculator;